import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, XCircle, Download, Loader2 } from "lucide-react";

export default function PartCostComparison() {
  const [file, setFile] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [summary, setSummary] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      toast.success(`Selected: ${selectedFile.name}`);
      
      // Try to extract dates from filename (format: xxx_DD.MM.YYYY_DD.MM.YYYY_xxx)
      const filename = selectedFile.name;
      const datePattern = /(\d{1,2})\.(\d{1,2})\.(\d{4}).*?(\d{1,2})\.(\d{1,2})\.(\d{4})/;
      const match = filename.match(datePattern);
      if (match) {
        const startDay = match[1].padStart(2, '0');
        const startMonth = match[2].padStart(2, '0');
        const startYear = match[3];
        const endDay = match[4].padStart(2, '0');
        const endMonth = match[5].padStart(2, '0');
        const endYear = match[6];
        
        setStartDate(`${startYear}-${startMonth}-${startDay}`);
        setEndDate(`${endYear}-${endMonth}-${endDay}`);
        toast.info(`📅 Dates auto-detected: ${startDay}/${startMonth}/${startYear} - ${endDay}/${endMonth}/${endYear}`);
      }
    }
  };

  const handleCompare = async () => {
    if (!file) {
      toast.error("Please select a Workiz export file");
      return;
    }
    if (!startDate || !endDate) {
      toast.error("Please select date range");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("start_date", startDate);
      formData.append("end_date", endDate);
      formData.append("upload_mode", "single");
      formData.append("selected_tech", "all");

      const response = await axios.post("/compare/workiz", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000
      });

      setResults(response.data.results);
      setSummary(response.data.summary);
      
      if (response.data.results.length === 0) {
        toast.success("✅ All parts match! No discrepancies found.");
      } else {
        toast.warning(`⚠️ Found ${response.data.results.length} mismatches`);
      }
    } catch (error) {
      console.error("Comparison error:", error);
      toast.error(error.response?.data?.detail || "Failed to compare files");
    } finally {
      setLoading(false);
    }
  };

  const exportResults = () => {
    if (!results || results.length === 0) {
      toast.error("No results to export");
      return;
    }

    const csvContent = [
      ["Job #", "Tech", "Date", "Workiz Parts", "App Parts", "Difference"].join(","),
      ...results.map(r => [
        r.job_id,
        `"${r.tech_name}"`,
        r.job_date,
        r.workiz_parts_cost,
        r.app_parts_cost,
        r.difference
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `part_mismatches_${startDate}_to_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported successfully!");
  };

  // Set default dates (last Monday to Sunday)
  const setLastWeek = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const lastMonday = new Date(today);
    lastMonday.setDate(today.getDate() - dayOfWeek - 6);
    const lastSunday = new Date(lastMonday);
    lastSunday.setDate(lastMonday.getDate() + 6);
    
    setStartDate(lastMonday.toISOString().split('T')[0]);
    setEndDate(lastSunday.toISOString().split('T')[0]);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileSpreadsheet className="w-5 h-5" />
            Part Cost Comparison
          </CardTitle>
          <p className="text-sm text-slate-500">
            Upload Workiz report to find price mismatches
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Workiz Finance Report (XLS)
            </label>
            <input
              type="file"
              accept=".xls,.xlsx,.csv"
              onChange={handleFileChange}
              className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
            />
            {file && (
              <p className="text-sm text-green-600 mt-1">✓ {file.name}</p>
            )}
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>
          </div>
          
          <Button variant="outline" size="sm" onClick={setLastWeek}>
            Last Week
          </Button>

          {/* Compare Button */}
          <Button 
            onClick={handleCompare} 
            disabled={loading || !file}
            className="w-full"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Comparing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Find Mismatches
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Summary */}
      {summary && (
        <Card>
          <CardContent className="pt-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="text-2xl font-bold text-slate-700">{summary.total_jobs_compared}</div>
                <div className="text-xs text-slate-500">Jobs Checked</div>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{summary.mismatches}</div>
                <div className="text-xs text-red-600">Mismatches</div>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg">
                <div className="text-2xl font-bold text-orange-600">${summary.total_difference.toFixed(2)}</div>
                <div className="text-xs text-orange-600">Total Diff</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Export Button */}
      {results && results.length > 0 && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={exportResults}>
            <Download className="w-4 h-4 mr-1" />
            Export CSV
          </Button>
        </div>
      )}

      {/* Results Table */}
      {results && results.length > 0 && (
        <Card>
          <CardContent className="pt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-2 font-medium">Job #</th>
                  <th className="pb-2 font-medium">Tech</th>
                  <th className="pb-2 font-medium text-right">Workiz</th>
                  <th className="pb-2 font-medium text-right">App</th>
                  <th className="pb-2 font-medium text-right">Diff</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, idx) => (
                  <tr key={idx} className="border-b hover:bg-red-50">
                    <td className="py-2 font-mono font-medium">{r.job_id}</td>
                    <td className="py-2">{r.tech_name}</td>
                    <td className="py-2 text-right font-mono">
                      ${r.workiz_parts_cost.toFixed(2)}
                    </td>
                    <td className="py-2 text-right font-mono">
                      ${r.app_parts_cost.toFixed(2)}
                    </td>
                    <td className={`py-2 text-right font-mono font-bold ${
                      r.difference > 0 ? 'text-red-600' : 'text-orange-600'
                    }`}>
                      {r.difference > 0 ? '+' : ''}{r.difference.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* No Mismatches */}
      {results && results.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <div className="text-4xl mb-2">✅</div>
            <div className="text-green-600 font-medium">All parts match!</div>
            <div className="text-slate-500 text-sm">No price discrepancies found</div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
